import React, { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  FormControl,
  FormError,
  FormLabel,
  Modal,
  ModalActions,
  ModalHeader,
  TextArea,
} from '@alefeducation/ui-elements-library-core';
import { Times } from '@alefeducation/ui-elements-library-icons';
import styled from 'styled-components';
import { useMutation } from '@apollo/react-hooks';
import i18n from 'i18next';
import PropTypes from 'prop-types';
import handleGraphQLMutationErrors from '../../helpers/handleGraphQLMutationErrors';
import resubmissionRequestCreate from '../../apollo/mutations/resubmissionRequestCreate.graphql';
import fireNotification from '../../utils/fireNotification';
import AssignmentContext from '../common/AssignmentContext/AssignmentContext';
import {
  useStudentSubmissionDispatch,
  useStudentSubmissionState,
} from './StudentSubmissionPageContext';
import {
  classAssignmentListQuery,
  classAssignmentSubmissionPageQuery,
  resubmissionMessageCharacterLimit,
} from './constants';
import './RequestResubmissionModal.scss';

const RequestResubmissionModal = ({ isStudentSubmission }) => {
  const {
    assignmentData: { assignmentId, assignmentType },
  } = useContext(AssignmentContext);
  const { t } = useTranslation();
  const dispatch = useStudentSubmissionDispatch();
  const state = useStudentSubmissionState();
  const [resubmissionMessage, setResubmissionMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState(null);
  const langCode = i18n.language;
  const studentId = state.reRequestedStudentId;
  const refetchQueryName = isStudentSubmission
    ? classAssignmentSubmissionPageQuery
    : classAssignmentListQuery;
  const [resubmissionRequestCreateMutation] = useMutation(
    resubmissionRequestCreate,
    {
      onError: error => {
        handleGraphQLMutationErrors(error, 'NetworkError', langCode);
      },
      onCompleted: () => {
        fireNotification('TeacherResubmissionSuccess');
      },
      refetchQueries: [refetchQueryName],
    },
  );

  const resubmissionMessageHandler = useCallback(event => {
    if (event.target.value.length > resubmissionMessageCharacterLimit) {
      setErrorMessage(
        t(`Exceeded limit of ${resubmissionMessageCharacterLimit} characters`),
      );
    } else {
      setErrorMessage(null);
    }
    setResubmissionMessage(event.target.value);
  }, []);
  const onConfirmHandler = useCallback(async () => {
    dispatch({ type: 'closeRequestResubmissionModal' });
    await resubmissionRequestCreateMutation({
      variables: {
        input: {
          assignmentId,
          studentId,
          message: resubmissionMessage,
        },
      },
    });
  });

  const onCancelHandler = useCallback(() => {
    dispatch({ type: 'closeRequestResubmissionModal' });
  });

  const assignmentTypeForPendo = assignmentType
    .split('_')
    .join('-')
    .toLowerCase();
  const enableSendButton = resubmissionMessage.length > 0 && !errorMessage;
  return (
    <div
      data-testid="request-resubmission-modal"
      className={'request-resubmission-modal'}
    >
      <Modal
        open={state.isRequestResubmissionModalOpen}
        size={'medium'}
        handleBackdropClick={onCancelHandler}
      >
        <ModalHeader>
          <Times
            height="14"
            width="14"
            color="primary.40"
            onClick={onCancelHandler}
            cursor="pointer"
            style={{ float: 'right' }}
            data-testid="close-button"
          />
        </ModalHeader>
        <FeedbackContainer>
          <Heading>{t('Request Resubmission')}</Heading>
          <FormControl appearance={errorMessage ? 'error' : 'regular'}>
            <FormLabel>{t('Message for your student')}</FormLabel>
            <TextArea
              placeholder={t('Type here')}
              data-testid={'request-resubmission-message-textarea'}
              value={resubmissionMessage}
              onChange={resubmissionMessageHandler.bind(this)}
              rows={4}
              className="request-resubmission__message-textarea usprivacy"
            />
            <FormError>{errorMessage}</FormError>
          </FormControl>
        </FeedbackContainer>
        <ModalActions>
          <Button
            variant="secondary"
            onClick={onCancelHandler}
            className={`data-analytics-${assignmentTypeForPendo}-resubmission-modal-cancel`}
          >
            {t('Cancel')}
          </Button>
          <Button
            onClick={onConfirmHandler}
            disabled={!enableSendButton}
            className={`data-analytics-${assignmentTypeForPendo}-resubmission-modal-confirm`}
          >
            {t('Send')}
          </Button>
        </ModalActions>
      </Modal>
    </div>
  );
};

export default RequestResubmissionModal;

RequestResubmissionModal.defaultProps = {
  isStudentSubmission: false,
};

RequestResubmissionModal.propTypes = {
  isStudentSubmission: PropTypes.bool,
};

const FeedbackContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: max-content;
  align-items: flex-start;
  gap: 16px;
  padding: 0 40px;

  & > div {
    width: 100%;
  }
`;

const Heading = styled.div`
  color: black;
  font-size: 18px;
  font-family: var(--font-family-signika);
  font-weight: 700;
  line-height: 24px;
  word-wrap: break-word;
`;
